/**
 * ChatMessageItem — renders a single chat bubble (user or assistant).
 * Assistant messages use react-markdown + remark-gfm for full Markdown support
 * (tables, headers, bold, code, lists).
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LoadingOutlined } from '@ant-design/icons';
import type { ChatMessage } from '@/services/chatApi';

interface Props {
  message: ChatMessage;
  /** If true, shows streaming animation for the last assistant turn */
  isStreaming?: boolean;
}

/** Styles injected once for markdown tables */
const MD_TABLE_STYLE = `
.delfos-md table {
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;
  margin: 8px 0;
}
.delfos-md th,
.delfos-md td {
  border: 1px solid #e0e0e0;
  padding: 4px 8px;
  text-align: left;
  white-space: nowrap;
}
.delfos-md th {
  background: #eef4ff;
  font-weight: 600;
  color: #1f4bd8;
}
.delfos-md tr:nth-child(even) td {
  background: #fafafa;
}
.delfos-md h3 {
  font-size: 13px;
  font-weight: 700;
  color: #1f4bd8;
  margin: 10px 0 4px;
  border-bottom: 1px solid #d7e5ff;
  padding-bottom: 2px;
}
.delfos-md h2 {
  font-size: 14px;
  font-weight: 700;
  color: #1539b8;
  margin: 12px 0 4px;
}
.delfos-md p { margin: 4px 0; }
.delfos-md ul, .delfos-md ol { margin: 4px 0; padding-left: 18px; }
.delfos-md li { margin: 2px 0; }
.delfos-md code {
  background: #f0f0f0;
  padding: 0 3px;
  border-radius: 3px;
  font-size: 0.85em;
}
.delfos-md pre code {
  background: none;
  padding: 0;
}
.delfos-md pre {
  background: #0f172a;
  color: #dbeafe;
  padding: 8px 12px;
  border-radius: 6px;
  overflow-x: auto;
  font-size: 11px;
}
`;

export function ChatMessageItem({ message, isStreaming }: Props) {
  const isUser = message.role === 'user';

  return (
    <>
      <style>{MD_TABLE_STYLE}</style>
      <div
        style={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          marginBottom: 8,
          padding: '0 16px',
        }}
      >
        <div
          style={{
            maxWidth: isUser ? '80%' : '95%',
            padding: '8px 12px',
            borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            background: isUser ? '#1f4bd8' : '#f5f7fb',
            color: isUser ? '#fff' : 'inherit',
            fontSize: 13,
            lineHeight: 1.6,
            wordBreak: 'break-word',
            overflowX: 'auto',
          }}
        >
          {isUser ? (
            <span>{message.content}</span>
          ) : (
            <div className="delfos-md">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.content}
              </ReactMarkdown>
            </div>
          )}
          {isStreaming && (
            <LoadingOutlined style={{ marginLeft: 6, fontSize: 11 }} />
          )}
        </div>
      </div>
    </>
  );
}
